import React, {useCallback, useEffect, useState} from "react";
import {useTitle} from "../../hooks/useTitle";
import {Alert, Button, Col, Form, Modal, Row, Table} from "react-bootstrap";
import AuthService from "../../http/AuthService";
import UserService from "../../http/UserService";
import {FaExpandAlt, FaRegEdit} from "react-icons/fa";
import {FiPlus} from "react-icons/fi";
import {FaLock, FaLockOpen, FaRegTrashCan} from "react-icons/fa6";
import Loading from "../../components/Loading";
import generatePassword from "../../utils/generatePassword";
import UserModalDelete from "../../components/modals/UserModalDelete";
import UserModalView from "../../components/modals/UserModalView";
import UserModalEdite from "../../components/modals/UserModalEdite";
import UserModalOff from "../../components/modals/UserModalOff";
import UserModalOn from "../../components/modals/UserModalOn";
import {useAuth} from "../../store/AuthContext";

const AdminUsers = () => {
    useTitle("Админ/Пользователи");
    const {isAuth, isLoading} = useAuth();

    const [loading, setLoading] = useState<boolean>(true);
    const [users, setUsers] = useState<any | null>(null);
    const [userId, setUserId] = useState<any | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [errorForm, setErrorForm] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [email, setEmail] = useState<string>("");
    const [lastname, setLastname] = useState<string>("");
    const [firstname, setFirstname] = useState<string>("");
    const [middlename, setMiddlename] = useState<string>("");
    const [fullscreen, setFullscreen] = useState<boolean>(false);
    const [showCreate, setShowCreate] = useState(false);
    const [showView, setShowView] = useState(false);
    const [showEdite, setShowEdite] = useState(false);
    const [showOff, setShowOff] = useState(false);
    const [showOn, setShowOn] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [validated, setValidated] = useState(false);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await UserService.query({
                includes: ['users_info'],
                options: {
                    where: { action: null },
                    order: [['id', 'ASC']]
                }
            });
            setUsers(data);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (isLoading || !isAuth) return;
            void fetchUsers();
        };

        void fetchData();
    }, [fetchUsers, isAuth, isLoading]);

    const handleSubmitCreate = async (event: React.SubmitEvent<HTMLFormElement>) => {
        const form = event.currentTarget;
        event.preventDefault();

        if (!form.checkValidity()) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        const fields = {
            email: email,
            password: generatePassword(),
            lastname: lastname,
            firstname: firstname,
            middlename: middlename,
        }

        try {
            await AuthService.registration(fields);
            setSuccess("Пользователь добавлен");
            setShowCreate(false);
            setValidated(false);

            await fetchUsers();
        } catch (err: any) {
            setErrorForm(err.response?.data?.message || err.message);
        }
    }

    if (isLoading || loading) {
        return <Loading />;
    }

    return (
        <>
            <div className={"d-flex jc-between mb-3"}>
                <h4>Пользователи</h4>
                <div>
                    <Button variant={"primary"} onClick={() => {
                        setShowCreate(true);
                        setErrorForm("");
                        setValidated(false);

                        setEmail("");
                        setLastname("");
                        setFirstname("");
                        setMiddlename("");
                    }}><FiPlus/>Добавить</Button>
                </div>
            </div>
            {error && <Alert variant={"danger"} dismissible={true}>{error}</Alert>}
            {success && <Alert variant={"success"} dismissible={true}>{success}</Alert>}
            {users
                ?
                <div className={"table-body scrollarea"}>
                    <Table bordered hover size="sm" className={"full"}>
                        <thead>
                        <tr>
                            <th style={{width: "1%"}}>ID</th>
                            <th>E-mail</th>
                            <th>ФИО</th>
                            <th style={{width: "90px"}}>Роль</th>
                            <th style={{width: "90px"}}>Статус</th>
                            <th style={{width: "145px"}}>Создан</th>
                            <th style={{width: "90px"}}></th>
                        </tr>
                        </thead>
                        <tbody>
                        {users.map((user: any) => {
                            let fio: string = "нет данных";
                            if (user?.users_info) {
                                fio = `${user.users_info.lastname ?? ""} ${user.users_info.firstname ?? ""} ${user.users_info.middlename ?? ""}`;
                            }
                            return (
                                <tr key={user.id}>
                                    <td>{user.id}</td>
                                    <td>
                                        <Button variant={"link"} onClick={() => {
                                            setUserId(user.id);
                                            setShowView(true);
                                        }}>
                                            {user.email}
                                        </Button>
                                    </td>
                                    <td>{fio}</td>
                                    <td>
                                        {user.role === "ADMIN" && 'Администратор'}
                                        {user.role === "USER" && 'Пользователь'}
                                    </td>
                                    <td>
                                        {user.active
                                            ?
                                            <span className={"text-success"}>Активен</span>
                                            :
                                            <span className={"text-danger"}>Отключен</span>
                                        }
                                    </td>
                                    <td>{new Date(user.createdAt).toLocaleString()}</td>
                                    <td>
                                        {user.role === "USER" && (
                                            <div className={"d-flex ai-center gap-3"}>
                                                {!user.active
                                                    ?
                                                    <FaLock title={"Включить"} className={"pointer"}
                                                            onClick={() => {
                                                                setUserId(user.id);
                                                                setShowOn(true);
                                                            }}
                                                    />
                                                    :
                                                    <FaLockOpen title={"Отключить"} className={"pointer"}
                                                                onClick={() => {
                                                                    setUserId(user.id);
                                                                    setShowOff(true);
                                                                }}
                                                    />
                                                }
                                                <FaRegEdit title={"Редактирование"} className={"pointer text-primary"}
                                                           onClick={() => {
                                                               setUserId(user.id);
                                                               setShowEdite(true);
                                                           }}
                                                />
                                                <FaRegTrashCan title={"Удалить"} className={"pointer text-danger"}
                                                               onClick={() => {
                                                                   setUserId(user.id);
                                                                   setShowDelete(true);
                                                               }}
                                                />
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </Table>
                </div>
                :
                <Alert variant={"secondary"}>Нет пользователей</Alert>
            }

            {showCreate && (
                <Modal show={showCreate} size={"lg"} fullscreen={fullscreen ? true : "md-down"} scrollable={true}
                       onHide={() => setShowCreate(false)}>
                    <div className="modal-header">
                        <h5 className="mb-0">Добавление пользователя</h5>
                        <div className={"d-flex gap-3"}>
                            <button type="button" className="btn-link" onClick={() => setFullscreen(!fullscreen)}>
                                <FaExpandAlt/>
                            </button>
                            <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                        </div>
                    </div>
                    <Form noValidate validated={validated} onSubmit={handleSubmitCreate}>
                        <Modal.Body>
                            {errorForm && <Alert variant={"danger"} dismissible={true}>{errorForm}</Alert>}
                            <Form.Group as={Row} className="mb-3" controlId="lastname">
                                <Form.Label column sm="2">Фамилия</Form.Label>
                                <Col sm="10">
                                    <Form.Control type="text" autoComplete={"off"}
                                                  value={lastname}
                                                  onChange={(e) => {
                                                      setLastname(e.target.value)
                                                  }}
                                    />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3" controlId="firstname">
                                <Form.Label column sm="2">Имя</Form.Label>
                                <Col sm="10">
                                    <Form.Control type="text" autoComplete={"off"}
                                                  value={firstname}
                                                  onChange={(e) => {
                                                      setFirstname(e.target.value)
                                                  }}
                                    />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3" controlId="middlename">
                                <Form.Label column sm="2">Отчество</Form.Label>
                                <Col sm="10">
                                    <Form.Control type="text" autoComplete={"off"}
                                                  value={middlename}
                                                  onChange={(e) => {
                                                      setMiddlename(e.target.value)
                                                  }}
                                    />
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3" controlId="email">
                                <Form.Label column sm="2">E-mail <span className={"text-danger"}>*</span></Form.Label>
                                <Col sm="10">
                                    <Form.Control type="email" autoComplete={"off"}
                                                  value={email}
                                                  onChange={(e) => {
                                                      setEmail(e.target.value)
                                                  }}
                                                  required={true}
                                    />
                                    <Form.Control.Feedback type="invalid">Поле обязательно для
                                        заполнения</Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3" controlId="password">
                                <Form.Label column sm="2">Пароль <span className={"text-danger"}>*</span></Form.Label>
                                <Col sm="10">
                                    <Form.Control type="password" autoComplete={"new-password"}
                                                  value={generatePassword()}
                                                  disabled={true}
                                                  required={true}
                                    />
                                    <Form.Control.Feedback type="invalid">Поле обязательно для
                                        заполнения</Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="primary" type="submit">Добавить</Button>
                            <Button variant="secondary" onClick={() => setShowCreate(false)}>Отмена</Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            )}

            {showView && <UserModalView userId={userId} visible={showView} setVisible={setShowView} refreshTable={fetchUsers}/>}

            {showEdite && <UserModalEdite userId={userId} visible={showEdite} setVisible={setShowEdite} refreshTable={fetchUsers}/>}

            {showOff && <UserModalOff userId={userId} visible={showOff} setVisible={setShowOff} refreshTable={fetchUsers}/>}

            {showOn && <UserModalOn userId={userId} visible={showOn} setVisible={setShowOn} refreshTable={fetchUsers}/>}

            {showDelete && <UserModalDelete userId={userId} visible={showDelete} setVisible={setShowDelete} refreshTable={fetchUsers}/>}
        </>
    );
};

export default AdminUsers;